/**
 * ServerPage - Server Terminal for Tableside Ordering
 * Features:
 * - 6-digit PIN staff authentication
 * - Table number entry (required)
 * - Customer lookup (optional)
 * - Full menu browsing with cart
 * - Skip payment - routes directly to KDS/Kitchen
 * - Light/Dark mode support
 * - Full item customization support
 * - REAL-TIME MENU SYNC: Auto-refreshes when admin makes changes
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Coffee, ShoppingBag, Plus, Minus, Trash2, X, User, Phone, Mail,
  Search, ChevronRight, Check, AlertCircle, LogOut, Users,
  UserCheck, UserPlus, Loader2, ArrowLeft, Sun, Moon,
  UtensilsCrossed, Send, Table, Gift, Clock, Crown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';
import { useTheme } from '@/context/ThemeContext';
import ItemCustomizationModal from '@/components/ordering/ItemCustomizationModal';
import { useMenuSync } from '@/hooks/useMenuSync';

// Import extracted components
import ServerTableSelect from '@/components/server/ServerTableSelect';
import ServerOrderComplete from '@/components/server/ServerOrderComplete';

// PIN Pad Component (shared with POS)
const PINPad = ({ onLogin, loading, title = "Server Terminal" }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');

  const handleDigit = (digit) => {
    if (pin.length < 6) {
      const newPin = pin + digit;
      setPin(newPin);
      setError('');
      
      if (newPin.length === 6) {
        handleSubmit(newPin);
      }
    }
  };

  const handleBackspace = () => {
    setPin(prev => prev.slice(0, -1));
    setError('');
  };

  const handleClear = () => {
    setPin('');
    setError('');
  };

  const handleSubmit = async (pinCode) => {
    const result = await onLogin(pinCode);
    if (!result.success) {
      setError(result.error || 'Invalid PIN');
      setPin('');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-900 via-indigo-800 to-indigo-900 dark:from-black dark:via-indigo-950 dark:to-black p-4" data-testid="server-login-screen">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-indigo-600 rounded-2xl mb-4">
            <UtensilsCrossed className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">{title}</h1>
          <p className="text-indigo-200">Enter your 6-digit PIN to sign in</p>
        </div>

        <div className="bg-indigo-800/50 rounded-2xl p-6 mb-6 backdrop-blur">
          <div className="flex justify-center gap-3 mb-4">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className={`w-12 h-14 rounded-xl flex items-center justify-center text-2xl font-bold transition-all ${
                  pin.length > i
                    ? 'bg-indigo-500 text-white'
                    : 'bg-indigo-700 text-indigo-400'
                }`}
              >
                {pin.length > i ? '•' : ''}
              </div>
            ))}
          </div>
          {error && (
            <div className="flex items-center justify-center gap-2 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-3 lg:gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
            <Button
              key={digit}
              variant="outline"
              onClick={() => handleDigit(String(digit))}
              disabled={loading}
              className="h-16 lg:h-20 text-2xl lg:text-3xl font-semibold bg-indigo-800 border-indigo-700 text-white hover:bg-indigo-700 hover:text-white"
              data-testid={`server-pin-digit-${digit}`}
            >
              {digit}
            </Button>
          ))}
          <Button
            variant="outline"
            onClick={handleClear}
            disabled={loading}
            className="h-16 lg:h-20 text-lg lg:text-xl font-medium bg-red-900/30 border-red-800 text-red-400 hover:bg-red-900/50 hover:text-red-300"
            data-testid="server-pin-clear"
          >
            Clear
          </Button>
          <Button
            variant="outline"
            onClick={() => handleDigit('0')}
            disabled={loading}
            className="h-16 lg:h-20 text-2xl lg:text-3xl font-semibold bg-indigo-800 border-indigo-700 text-white hover:bg-indigo-700 hover:text-white"
            data-testid="server-pin-digit-0"
          >
            0
          </Button>
          <Button
            variant="outline"
            onClick={handleBackspace}
            disabled={loading}
            className="h-16 lg:h-20 text-lg lg:text-xl font-medium bg-indigo-800 border-indigo-700 text-white hover:bg-indigo-700"
            data-testid="server-pin-backspace"
          >
            ←
          </Button>
        </div>

        {loading && (
          <div className="flex items-center justify-center mt-6 text-indigo-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Signing in...
          </div>
        )}

        <p className="text-center text-indigo-300 text-sm mt-8">
          Contact your manager if you forgot your PIN
        </p>
      </div>
    </div>
  );
};

// TableSelect component extracted to @/components/server/ServerTableSelect.jsx

// Main Server Terminal Component
export default function ServerPage() {
  const { theme, toggleTheme } = useTheme();
  
  // Auth State
  const [staff, setStaff] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  
  // View State
  const [view, setView] = useState('login'); // login, table-select, customer-lookup, ordering
  const [tableInfo, setTableInfo] = useState(null); // { table: '1', guests: 2 }
  const [tableSelectRefresh, setTableSelectRefresh] = useState(0); // Counter to force TableSelect refresh
  
  // Menu & Cart State
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Order State
  const [orderProcessing, setOrderProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(null);
  const [notes, setNotes] = useState('');
  
  // Customization modal state
  const [selectedItem, setSelectedItem] = useState(null);
  const [showCustomization, setShowCustomization] = useState(false);
  const [availableAddOns, setAvailableAddOns] = useState([]);
  
  // Customer & Coffee Pass State
  const [customer, setCustomer] = useState(null);
  const [customerCoffeePass, setCustomerCoffeePass] = useState(null);
  const [showCustomerLookup, setShowCustomerLookup] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [searchingCustomer, setSearchingCustomer] = useState(false);
  
  // Owner Order Mode
  const [isOwnerOrder, setIsOwnerOrder] = useState(false);

  // Handle PIN login
  const handleLogin = async (pin) => {
    setLoginLoading(true);
    try {
      // Use device-specific login to validate role permissions
      const res = await api.post('/api/device/login', { pin, device_type: 'server' });
      setStaff(res.data.staff);
      setView('table-select');
      toast.success(res.data.message);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Invalid PIN' };
    } finally {
      setLoginLoading(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    if (staff) {
      try {
        await api.post(`/api/pos/logout?staff_id=${staff.id}`);
      } catch (err) {
        console.error('Logout error:', err);
      }
    }
    setStaff(null);
    setTableInfo(null);
    setCart([]);
    setView('login');
  };

  // Fetch menu items
  const fetchMenu = async () => {
    try {
      setLoading(true);
      const cacheBuster = `?_t=${Date.now()}`;
      const res = await api.get(`/menu${cacheBuster}`);
      const available = res.data.filter(item => item.is_available !== false);
      setMenuItems(available);
      
      const cats = [...new Set(available.map(item => item.category))].filter(Boolean);
      setCategories(cats);
      
      // Fetch add-ons for customization
      const addOnsRes = await api.get(`/add-ons${cacheBuster}`);
      setAvailableAddOns(addOnsRes.data.filter(a => a.is_available !== false));
    } catch (err) {
      console.error('Failed to fetch menu:', err);
    } finally {
      setLoading(false);
    }
  };

  // Refresh add-ons periodically (every 60 seconds) to pick up admin changes
  const refreshAddOns = useCallback(async () => {
    try {
      const cacheBuster = `?_t=${Date.now()}`;
      const addOnsRes = await api.get(`/add-ons${cacheBuster}`);
      setAvailableAddOns(addOnsRes.data.filter(a => a.is_available !== false));
    } catch (err) {
      console.error('Failed to refresh add-ons:', err);
    }
  }, []);

  // Auto-refresh add-ons every 60 seconds
  useEffect(() => {
    if (staff) {
      const interval = setInterval(refreshAddOns, 60000);
      return () => clearInterval(interval);
    }
  }, [staff, refreshAddOns]);

  // ============== REAL-TIME MENU SYNC ==============
  // Listen for menu changes from admin panel and auto-refresh
  const handleMenuUpdate = useCallback((data) => {
    console.log('[Server] Menu update received:', data);
    
    const updateType = data.update_type || 'unknown';
    const itemName = data.data?.item_name;
    const action = data.data?.action || 'updated';
    
    let message = 'Menu updated';
    if (updateType === 'menu_item' && itemName) {
      message = `Menu item ${action}: ${itemName}`;
    } else if (updateType === 'add_on') {
      message = 'Add-ons updated';
    } else if (updateType === 'category') {
      message = 'Categories updated';
    }
    
    toast.info(message, { 
      description: 'Refreshing menu...',
      duration: 2000 
    });
    
    // Refetch menu data
    fetchMenu();
  }, []);

  // Connect to menu sync WebSocket (only when staff is logged in)
  useMenuSync(staff ? handleMenuUpdate : null);

  // Table selection - go to customer lookup first
  const handleSelectTable = (info) => {
    setTableInfo(info);
    setView('customer-lookup');
  };
  
  // Skip customer lookup and go to ordering
  const skipCustomerLookup = () => {
    setView('ordering');
  };
  
  // Go to ordering after selecting customer
  const proceedToOrdering = () => {
    setView('ordering');
  };

  // Customer search effect - automatically search when customerSearch changes
  useEffect(() => {
    if (!customerSearch || customerSearch.length < 2) {
      setCustomerSearchResults([]);
      setSearchingCustomer(false);
      return;
    }
    
    setSearchingCustomer(true);
    let cancelled = false;
    
    // Debounce timer
    const timer = setTimeout(async () => {
      try {
        console.log('[ServerPage] Searching for:', customerSearch);
        const res = await api.get(`/contacts/search`, {
          params: { q: customerSearch, limit: 10 },
          timeout: 6000 // 6 second timeout
        });
        if (!cancelled) {
          console.log('[ServerPage] Contacts found:', res.data?.contacts?.length);
          const contacts = res.data?.contacts || [];
          setCustomerSearchResults(Array.isArray(contacts) ? contacts : []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[ServerPage] Customer search failed:', err);
          setCustomerSearchResults([]);
        }
      } finally {
        if (!cancelled) {
          setSearchingCustomer(false);
        }
      }
    }, 300);
    
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [customerSearch]);

  // Check Coffee Pass status for a customer
  const checkCoffeePassStatus = async (email, phone) => {
    if (!email && !phone) {
      setCustomerCoffeePass(null);
      return;
    }
    
    try {
      const queryParam = email 
        ? `email=${encodeURIComponent(email)}` 
        : `phone=${encodeURIComponent(phone)}`;
      const res = await fetch(`/api/coffee-pass/check?${queryParam}`);
      if (!res.ok) throw new Error('Coffee pass check failed');
      const data = await res.json();
      setCustomerCoffeePass(data || null);
      return data;
    } catch (err) {
      console.error('Failed to check coffee pass:', err);
      setCustomerCoffeePass(null);
      return null;
    }
  };

  // Select customer and check their Coffee Pass
  const handleSelectCustomer = async (cust) => {
    setCustomer(cust);
    setShowCustomerLookup(false);
    setCustomerSearch('');
    setCustomerSearchResults([]);
    
    // Check for Coffee Pass
    const passData = await checkCoffeePassStatus(cust.email, cust.phone);
    
    if (passData) {
      if (passData.can_redeem_now) {
        // Show toast but DON'T open modal - let them browse the menu naturally
        toast.success('Bula Coffee Pass Ready!', {
          description: `${cust.first_name || cust.name || 'Customer'} can redeem their free coffee. Select any coffee from the menu!`,
          icon: <Coffee className="w-4 h-4" />,
          duration: 6000
        });
      } else if (passData.can_upgrade_to_redeem) {
        // Standard pass holder in afternoon window - offer upgrade!
        toast.info(`Upgrade Available - $${passData.upgrade_info?.upgrade_price || 40}`, {
          description: `${cust.first_name || cust.name || 'Customer'} has a morning-only pass. Upgrade to unlock afternoon redemption!`,
          icon: <Crown className="w-4 h-4" />,
          duration: 8000
        });
      } else if (passData.has_active_pass) {
        if (passData.already_redeemed_today) {
          toast.info('Coffee Pass already redeemed today', {
            description: `${cust.first_name || cust.name || 'Customer'} enjoyed their free coffee earlier.`,
            icon: <Coffee className="w-4 h-4" />
          });
        } else {
          toast.info('Coffee Pass - Outside redemption hours', {
            description: passData.window_message || 'Redemption available 7:00 AM - 10:00 AM or 1:00 PM - 3:30 PM',
            icon: <Clock className="w-4 h-4" />
          });
        }
      }
    }
  };

  // Clear customer
  const handleClearCustomer = () => {
    setCustomer(null);
    setCustomerCoffeePass(null);
  };

  // Handle item click - open customization modal
  const handleItemClick = (item) => {
    setSelectedItem(item);
    setShowCustomization(true);
  };

  // Handle add to cart from customization modal
  const handleAddCustomizedItem = (cartItem) => {
    // Helper to get size identifier for comparison (compare by name/id, not object reference)
    const getSizeId = (size) => {
      if (!size) return null;
      return size.id || size.name || JSON.stringify(size);
    };
    
    setCart(prev => {
      const hasCustomizations = cartItem.modifiers || cartItem.removedIngredients || 
                                cartItem.extraIngredients || cartItem.addOns || cartItem.specialNotes;
      
      if (!hasCustomizations) {
        const existing = prev.find(c => 
          c.item_id === cartItem.item_id && 
          getSizeId(c.selectedSize) === getSizeId(cartItem.selectedSize) &&
          !c.modifiers && !c.removedIngredients && !c.extraIngredients && !c.addOns && !c.specialNotes
        );
        
        if (existing) {
          return prev.map(c => c === existing ? { ...c, quantity: c.quantity + cartItem.quantity } : c);
        }
      }
      
      return [...prev, cartItem];
    });
    
    toast.success(`Added ${cartItem.quantity}x ${cartItem.name}`, { duration: 1500 });
  };

  // Legacy cart operations (kept for backwards compatibility)
  const addToCart = (item) => {
    // Use the BASE item.price - not sizes (sizes are upsells)
    const price = item.price || 0;
    
    // Generate unique cart item ID including price to keep different sizes separate
    const cartItemId = `${item.id}-${price}`;
    
    setCart(prev => {
      // Only merge if EXACT same item (same ID AND same price) with no customizations
      const existing = prev.find(c => c.item_id === cartItemId && !c.modifiers && !c.removedIngredients);
      if (existing) {
        return prev.map(c => 
          c.item_id === cartItemId && !c.modifiers && !c.removedIngredients
            ? { ...c, quantity: c.quantity + 1 }
            : c
        );
      }
      return [...prev, {
        item_id: cartItemId,
        name: item.name,
        price,
        quantity: 1,
        category: item.category
      }];
    });
    
    toast.success(`Added ${item.name}`, { duration: 1500 });
  };

  const updateQuantity = (itemId, delta) => {
    setCart(prev => {
      return prev.map(item => {
        if (item.item_id === itemId) {
          const newQty = item.quantity + delta;
          if (newQty <= 0) return null;
          return { ...item, quantity: newQty };
        }
        return item;
      }).filter(Boolean);
    });
  };

  const removeFromCart = (itemId) => {
    setCart(prev => prev.filter(item => item.item_id !== itemId));
  };

  const clearCart = () => {
    setCart([]);
    setNotes('');
  };

  // Calculate total
  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  // Check if cart is ONLY free coffee pass redemptions (no paid items)
  const isOnlyFreeCoffeePass = cart.length > 0 && cart.every(item => 
    item.price === 0 && (item.is_coffee_club_redemption || item.isCoffeePassRedemption)
  );

  // Send order to stations (handles both dine-in and takeaway)
  const sendToKitchen = async () => {
    if (cart.length === 0) return;
    
    // Validate staff authentication before sending
    if (!staff?.id) {
      toast.error('Session expired. Please login again.');
      setStaff(null);
      setView('login');
      return;
    }
    
    setOrderProcessing(true);
    try {
      const isTakeaway = tableInfo?.orderType === 'takeaway';
      const hasPaidItems = cartTotal > 0;
      
      // CHECK: Does this table already have an existing unpaid order?
      // If yes, ADD items to that order instead of creating a new one
      // Note: Backend may return .id or .order_id depending on endpoint
      const existingOrderId = tableInfo?.lastOrderId || tableInfo?.existingOrders?.[0]?.order_id || tableInfo?.existingOrders?.[0]?.id;
      
      if (!isTakeaway && existingOrderId) {
        // ========== ADD TO EXISTING ORDER ==========
        // Table already has an order - add these items to it
        console.log(`Server: Adding ${cart.length} items to existing order ${existingOrderId}`);
        
        const addItemsData = {
          order_id: existingOrderId,
          items: cart.map(item => ({
            item_id: item.item_id,
            name: item.name,
            quantity: item.quantity,
            price: item.price,
            category: item.category,
            size: item.size || item.selectedSize || null,
            selectedSize: item.selectedSize || item.size || null,
            modifiers: item.modifiers || null,
            special_instructions: item.specialInstructions || item.specialNotes || item.special_instructions || null,
            specialNotes: item.specialNotes || item.specialInstructions || item.special_instructions || null,
            add_ons: item.add_ons || item.addOns || item.selectedAddOns || null,
            addOns: item.addOns || item.add_ons || item.selectedAddOns || null,
            removed_ingredients: item.removed_ingredients || item.removedIngredients || null,
            removedIngredients: item.removedIngredients || item.removed_ingredients || null,
            extra_ingredients: item.extra_ingredients || item.extraIngredients || null,
            extraIngredients: item.extraIngredients || item.extra_ingredients || null,
            is_coffee_club_redemption: item.is_coffee_club_redemption || item.isCoffeePassRedemption || false
          }))
        };
        
        // Use add-items endpoint - ONLY prints the NEW items, not the entire order
        // Must include X-Staff-ID header for authentication
        const res = await api.post('/api/pos/order/add-items', addItemsData, {
          headers: { 'X-Staff-ID': staff?.id }
        });
        const result = res.data;
        
        // Update table info with new totals
        setTableInfo(prev => {
          if (!prev) {
            console.warn('tableInfo was null during add-items update');
            return prev;
          }
          return {
            ...prev,
            existingOrders: (prev.existingOrders || []).map(order => 
              order.order_id === existingOrderId 
                ? { ...order, items: [...(order.items || []), ...cart], total: (order.total || 0) + cartTotal }
                : order
            ),
            existingTotal: (prev.existingTotal || 0) + cartTotal
          };
        });
        
        // Clear cart for new items
        setCart([]);
        setNotes('');
        
        toast.success(`${result.items_added} items added to Order #${tableInfo?.lastOrderNumber || (existingOrderId ? existingOrderId.slice(0,8) : 'Unknown')}!`, {
          description: `Table ${tableInfo?.table} - New total: $${result.new_total?.toFixed(2) || '0.00'}`,
          duration: 5000
        });
        
        return;
      }
      
      // ========== CREATE NEW ORDER ==========
      // Either it's takeaway, or dine-in with no existing order
      // Determine order flow:
      // - Takeaway with paid items -> POS queue for payment
      // - Takeaway with only free coffee pass -> Direct to barista (no POS queue needed)
      // - Dine-in -> Always POS queue with table number
      // - Owner order -> No charge, prints to stations
      
      const orderData = {
        items: cart.map(item => {
          // CRITICAL FIX: Ensure Coffee Pass redemptions have correct price ($0 or upgrade cost only)
          const isCoffeePassItem = item.isCoffeePassRedemption || item.isCoffeeClubRedemption || 
                                    item.is_coffee_pass || item.is_coffee_club_redemption;
          // Coffee pass items should already have price=0 (or upgrade cost) from the modal
          // But if somehow the price is the full menu price, force it to $0
          let itemPrice = item.price;
          if (isCoffeePassItem && itemPrice > 10) {
            // If price > $10 for a coffee pass item, something went wrong - reset to $0
            console.warn(`Coffee pass item "${item.name}" has unexpected price $${itemPrice}, resetting to $0`);
            itemPrice = 0;
          }
          
          return {
            item_id: item.item_id,
            name: item.name,
            quantity: item.quantity,
            price: itemPrice,
            category: item.category,
            // Include ALL customization data for station printing
            size: item.size || item.selectedSize || null,
            selectedSize: item.selectedSize || item.size || null,
            selectedMilk: item.selectedMilk || null,
            modifiers: item.modifiers || null,
            special_instructions: item.specialInstructions || item.specialNotes || item.special_instructions || null,
            specialNotes: item.specialNotes || item.specialInstructions || item.special_instructions || null,
            add_ons: item.add_ons || item.addOns || item.selectedAddOns || null,
            addOns: item.addOns || item.add_ons || item.selectedAddOns || null,
            selectedAddOns: item.selectedAddOns || item.addOns || item.add_ons || [],
            removed_ingredients: item.removed_ingredients || item.removedIngredients || null,
            removedIngredients: item.removedIngredients || item.removed_ingredients || null,
            extra_ingredients: item.extra_ingredients || item.extraIngredients || null,
            extraIngredients: item.extraIngredients || item.extra_ingredients || null,
            // CRITICAL: Preserve ALL Coffee Pass flags for proper tracking
            is_coffee_club_redemption: isCoffeePassItem,
            isCoffeeClubRedemption: isCoffeePassItem,
            is_coffee_pass: isCoffeePassItem,
            isCoffeePassRedemption: isCoffeePassItem,
            passRedemptionId: item.passRedemptionId || item.pass_redemption_id || null,
            plan_type: item.plan_type || null,
            product_id: item.product_id || null
          };
        }),
        customer_id: customer?.id || null,
        customer_name: isOwnerOrder ? 'OWNER' : (customer?.name || customer?.first_name || (isTakeaway ? 'Take Away' : `Table ${tableInfo?.table}`)),
        customer_email: customer?.email || null,
        customer_phone: customer?.phone || null,
        subtotal: isOwnerOrder ? 0 : cartTotal,  // Owner orders are $0
        total: isOwnerOrder ? 0 : cartTotal,  // Owner orders are $0
        payment_method: isOwnerOrder ? 'owner' : ((isTakeaway && isOnlyFreeCoffeePass) ? 'coffee_pass' : 'pending'),
        payment_status: isOwnerOrder ? 'owner_comp' : ((isTakeaway && isOnlyFreeCoffeePass) ? 'paid' : 'pending'),
        order_type: isTakeaway ? 'takeaway' : 'dine-in',
        table_number: isTakeaway ? null : tableInfo?.table,
        staff_id: staff?.id,
        staff_name: staff?.name,
        source: 'server', // Identify this came from server tablet
        is_owner_order: isOwnerOrder  // Owner order flag
      };

      // Use the POS send-to-kitchen endpoint (creates order + prints to all stations)
      const res = await api.post('/api/pos/orders/send-to-kitchen', orderData);
      const order = res.data;

      // For DINE-IN orders: Keep the order parked on the tablet
      // Staff can add more items to the same table
      if (!isTakeaway) {
        // Update tableInfo with the new order info so it shows as "parked"
        setTableInfo(prev => {
          if (!prev) {
            console.warn('tableInfo was null during order update');
            return prev;
          }
          
          // Safely handle the response data
          const orderId = order?.order_id || order?.id || '';
          const orderNumber = order?.order_number || '';
          
          // Safely construct the existing orders array
          const newOrder = {
            order_id: orderId,
            order_number: orderNumber,
            items: cart || [],
            total: cartTotal || 0,
            status: 'sent'
          };
          
          const existingOrders = Array.isArray(prev.existingOrders) ? prev.existingOrders : [];
          
          return {
            ...prev,
            existingOrders: [...existingOrders, newOrder],
            existingTotal: (Number(prev.existingTotal) || 0) + (Number(cartTotal) || 0),
            lastOrderNumber: orderNumber,
            lastOrderId: orderId
          };
        });
        
        // Clear the cart for new items but STAY on ordering view
        setCart([]);
        setNotes('');
        
        toast.success(`Order #${order.order_number} sent to stations!`, {
          description: `Table ${tableInfo?.table} - Add more items or view at POS`,
          duration: 5000
        });
        
        // Don't set orderComplete for dine-in - stay on ordering view
        return;
      }
      
      // For TAKEAWAY: Show completion screen
      // For OWNER orders: Show success and reset
      if (isOwnerOrder) {
        toast.success(`👑 Owner Order #${order.order_number} sent to stations!`, {
          description: 'No charge - food/drinks being prepared',
          duration: 4000
        });
        setCart([]);
        setNotes('');
        setIsOwnerOrder(false);  // Reset owner mode
        return;
      }
      
      const completionInfo = {
        order_number: order.order_number,
        order_id: order.order_id,
        total: cartTotal,
        table: null,
        orderType: 'takeaway',
        isFreeCoffeePass: isOnlyFreeCoffeePass
      };
      
      setOrderComplete(completionInfo);
      
      if (isOnlyFreeCoffeePass) {
        toast.success(`Order #${order.order_number} sent to Barista! (Free Coffee Pass)`);
      } else {
        toast.success(`Order #${order.order_number} sent to POS for payment!`);
      }
    } catch (err) {
      console.error('Order error:', err);
      toast.error(err.response?.data?.detail || 'Order failed. Please try again.');
    } finally {
      setOrderProcessing(false);
    }
  };

  // Handle opening an existing table to add more items
  const handleOpenExistingTable = (table) => {
    // Get the last order ID from the orders array (most recent first)
    const lastOrderId = table.orders?.[0]?.id || table.last_order_id;
    
    // Set up the table info and go to ordering view
    setTableInfo({
      table: table.table_number,
      guests: table.guest_count || 2,
      orderType: 'dine-in',
      existingOrders: table.orders,
      existingTotal: table.total_amount,
      lastOrderId: lastOrderId  // IMPORTANT: Needed for add-items API
    });
    setCart([]); // Fresh cart for new items
    setNotes('');
    setView('ordering');
    toast.info(`Adding items to Table ${table.table_number}`);
  };

  // Start new order (same table or new table)
  const startNewOrder = (newTable = false) => {
    setOrderComplete(null);
    setCart([]);
    setNotes('');
    if (newTable) {
      setTableInfo(null);
      setTableSelectRefresh(prev => prev + 1); // Force TableSelect to refresh
      setView('table-select');
    } else {
      // Adding more to same table - keep tableInfo and go back to ordering
      // The table is now "parked" with the existing order
      setView('ordering');
    }
  };
  
  // Go back to table select to see all open tables
  const goToTableSelect = () => {
    setOrderComplete(null);
    setCart([]);
    setNotes('');
    setTableInfo(null);
    setTableSelectRefresh(prev => prev + 1); // Force TableSelect to refresh
    setView('table-select');
  };

  // Load menu on entering ordering view - ALWAYS refresh to get latest prices
  useEffect(() => {
    if (view === 'ordering') {
      fetchMenu(); // Always fetch fresh menu data
    }
  }, [view]);

  // Filter menu items
  const filteredItems = menuItems.filter(item => {
    if (activeCategory !== 'all' && item.category !== activeCategory) return false;
    if (search && !item.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Category tabs
  const categoryTabs = [
    { id: 'all', label: 'All', count: menuItems.length },
    ...categories.map(cat => ({
      id: cat,
      label: cat.charAt(0).toUpperCase() + cat.slice(1).replace(/-/g, ' '),
      count: menuItems.filter(i => i.category === cat).length
    }))
  ];

  // Render based on view state
  if (view === 'login') {
    return <PINPad onLogin={handleLogin} loading={loginLoading} title="Server Terminal" />;
  }

  if (view === 'table-select') {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
        {/* Top Bar */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UtensilsCrossed className="w-6 h-6 text-indigo-600" />
            <span className="font-semibold text-gray-900 dark:text-white">Server Terminal</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Signed in as <span className="font-medium text-gray-900 dark:text-white">{staff?.name}</span>
            </span>
            <Button variant="ghost" size="sm" onClick={toggleTheme} className="text-gray-500">
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-red-500" data-testid="server-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <ServerTableSelect
          key={`table-select-${tableSelectRefresh}`}  // Force remount when counter changes
          onSelectTable={handleSelectTable}
          onBack={handleLogout}
          onOpenTable={handleOpenExistingTable}
          staffId={staff?.id}
        />
      </div>
    );
  }

  // Customer Lookup Screen (after table selection, before menu)
  if (view === 'customer-lookup') {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
        {/* Top Bar */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UtensilsCrossed className="w-6 h-6 text-indigo-600" />
            <span className="font-semibold text-gray-900 dark:text-white">Server Terminal</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">• Table {tableInfo?.table}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Signed in as <span className="font-medium text-gray-900 dark:text-white">{staff?.name}</span>
            </span>
            <Button variant="ghost" size="sm" onClick={toggleTheme} className="text-gray-500">
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        <div className="p-6 max-w-2xl mx-auto">
          <button
            onClick={() => {
              setTableSelectRefresh(prev => prev + 1);
              setView('table-select');
            }}
            className="flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-6"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Table Selection
          </button>

          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <Gift className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Customer Lookup</h2>
                <p className="text-gray-500 dark:text-gray-400">Check for loyalty rewards & Coffee Pass</p>
              </div>
            </div>

            {/* Search Input */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                placeholder="Search by phone, email, or name..."
                className="pl-10 h-12"
                data-testid="server-customer-search"
              />
              {searchingCustomer && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-indigo-500" />
              )}
            </div>

            {/* Search Results */}
            {customerSearchResults.length > 0 && (
              <div className="border dark:border-gray-700 rounded-xl overflow-hidden mb-4">
                {customerSearchResults.map((c, idx) => (
                  <button
                    key={c.id || idx}
                    onClick={() => {
                      handleSelectCustomer(c);
                      proceedToOrdering();
                    }}
                    className="w-full p-4 text-left hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border-b last:border-b-0 dark:border-gray-700 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {c.first_name || c.name || 'Guest'} {c.last_name || ''}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {c.phone || c.email || 'No contact info'}
                        </p>
                      </div>
                      {c.loyalty_tier && (
                        <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 rounded-full">
                          {c.loyalty_tier}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* No Results */}
            {customerSearch.length >= 2 && !searchingCustomer && customerSearchResults.length === 0 && (
              <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                <User className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No customers found</p>
              </div>
            )}

            {/* Selected Customer */}
            {customer && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                      <UserCheck className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-green-900 dark:text-green-100">
                        {customer.first_name || customer.name} {customer.last_name || ''}
                      </p>
                      <p className="text-sm text-green-700 dark:text-green-300">
                        {customer.phone || customer.email}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setCustomer(null);
                      setCustomerCoffeePass(null);
                    }}
                    className="text-green-600 hover:text-green-800"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                {customerCoffeePass?.has_active_pass && (
                  <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-800">
                    <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                      <Coffee className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {customerCoffeePass.can_redeem_now 
                          ? 'Coffee Pass Ready to Redeem!' 
                          : customerCoffeePass.already_redeemed_today 
                            ? 'Already redeemed today' 
                            : 'Coffee Pass Active'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={proceedToOrdering}
              className="w-full h-14 text-lg bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="server-proceed-btn"
            >
              {customer ? `Continue with ${customer.first_name || customer.name || 'Customer'}` : 'Continue as Guest'}
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            
            {!customer && (
              <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                Skip lookup to order as guest
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Order Complete Screen
  if (orderComplete) {
    return (
      <ServerOrderComplete
        orderComplete={orderComplete}
        onAddMore={(table) => startNewOrder(false)}
        onViewTables={goToTableSelect}
        onNewOrder={() => startNewOrder(true)}
      />
    );
  }

  // Main Ordering View
  return (
    <div className="h-screen flex flex-col bg-gray-100 dark:bg-gray-900" data-testid="server-ordering-screen">
      {/* Top Bar */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <UtensilsCrossed className="w-6 h-6 text-indigo-600" />
          <span className="font-semibold text-gray-900 dark:text-white">Server Terminal</span>
        </div>
        
        {/* Table Badge */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-4 py-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-full">
            <Table className="w-4 h-4 text-indigo-600" />
            <span className="text-sm font-bold text-indigo-700 dark:text-indigo-400">Table {tableInfo?.table}</span>
            <span className="text-xs text-indigo-500">({tableInfo?.guests} guests)</span>
            <button 
              onClick={() => {
                setCart([]);
                setTableSelectRefresh(prev => prev + 1);
                setView('table-select');
              }} 
              className="ml-1 text-indigo-500 hover:text-indigo-700"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            <span className="font-medium text-gray-900 dark:text-white">{staff?.name}</span>
          </span>
          <Button variant="ghost" size="sm" onClick={toggleTheme} className="text-gray-500">
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-red-500" data-testid="server-logout-btn">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Existing Order Banner - show if table has existing orders */}
      {tableInfo?.existingOrders && tableInfo.existingOrders.length > 0 && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2 flex items-center justify-between" data-testid="existing-order-banner">
          <div className="flex items-center gap-3 text-white">
            <Clock className="w-5 h-5" />
            <span className="font-medium">
              Table {tableInfo.table} has existing items: ${tableInfo.existingTotal?.toFixed(2) || '0.00'}
            </span>
            <span className="text-amber-100 text-sm">
              ({tableInfo.existingOrders.reduce((sum, o) => sum + (o.items?.length || 0), 0)} items across {tableInfo.existingOrders.length} order(s))
            </span>
          </div>
          <span className="text-amber-100 text-sm">Adding new items to this table</span>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Menu Section */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Search & Categories */}
          <div className="p-4 lg:p-5 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <div className="flex gap-4 items-center">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search menu..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 h-11 lg:h-12 bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600"
                  data-testid="server-menu-search"
                />
              </div>
            </div>
            
            {/* Category Tabs */}
            <div className="flex gap-2 lg:gap-3 mt-4 overflow-x-auto pb-2">
              {categoryTabs.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-4 py-2.5 lg:px-5 lg:py-3 rounded-lg text-sm lg:text-base font-medium whitespace-nowrap transition-all ${
                    activeCategory === cat.id
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Menu Grid */}
          <div className="flex-1 overflow-y-auto p-4 lg:p-5">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-5">
                {filteredItems.map(item => {
                  const hasCustomizations = item.sizes?.length > 0 || item.modifiers?.length > 0 || item.ingredients?.length > 0;
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleItemClick(item)}
                      className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden text-left hover:ring-2 hover:ring-indigo-500 transition-all active:scale-95 shadow-sm"
                      data-testid={`server-item-${item.id}`}
                    >
                      <div className="relative">
                        {item.image ? (
                          <img 
                            src={item.image} 
                            alt={item.name}
                            className="w-full h-32 lg:h-40 object-cover"
                          />
                        ) : (
                          <div className="w-full h-32 lg:h-40 bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                            <Coffee className="w-10 h-10 lg:w-12 lg:h-12 text-gray-400" />
                          </div>
                        )}
                        {hasCustomizations && (
                          <span className="absolute top-2 right-2 px-2 py-1 bg-indigo-600 text-white text-xs lg:text-sm font-medium rounded-full">
                            Customize
                          </span>
                        )}
                      </div>
                      <div className="p-3 lg:p-4">
                        <h3 className="font-medium text-gray-900 dark:text-white text-sm lg:text-base line-clamp-2">{item.name}</h3>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-lg lg:text-xl font-bold text-indigo-600">
                            ${(item.price || item.sizes?.[0]?.price || 0).toFixed(2)}
                            {item.sizes?.length > 1 && '+'}
                          </span>
                          <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-full bg-indigo-600 flex items-center justify-center">
                            <Plus className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Cart Section */}
        <div className="w-96 lg:w-[420px] bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 flex flex-col">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <ShoppingBag className="w-5 h-5" />
                Table {tableInfo?.table} Order
              </h2>
              {cart.length > 0 && (
                <Button variant="ghost" size="sm" onClick={clearCart} className="text-red-500 hover:text-red-600">
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Customer & Coffee Pass Section */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            {!customer ? (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search customer for Coffee Pass..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className="pl-9 h-10 text-sm"
                    data-testid="server-customer-search"
                  />
                </div>
                
                {/* Search Results */}
                {customerSearchResults.length > 0 && (
                  <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 max-h-48 overflow-y-auto">
                    {customerSearchResults.map(cust => (
                      <button
                        key={cust.contact_id || cust.email}
                        onClick={() => handleSelectCustomer(cust)}
                        className="w-full p-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600 border-b border-gray-100 dark:border-gray-600 last:border-0"
                        data-testid={`server-customer-result-${cust.contact_id}`}
                      >
                        <p className="font-medium text-sm text-gray-900 dark:text-white">{cust.name || cust.first_name}</p>
                        <p className="text-xs text-gray-500">{cust.email}</p>
                      </button>
                    ))}
                  </div>
                )}
                
                {searchingCustomer && (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-indigo-600" />
                    <span className="font-medium text-sm text-gray-900 dark:text-white">
                      {customer.name || customer.first_name}
                    </span>
                  </div>
                  <button 
                    onClick={handleClearCustomer}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Coffee Pass Status - NEW: Direct to menu, not modal */}
                {customerCoffeePass?.has_active_pass && (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs rounded-full font-medium flex items-center gap-1 ${
                        customerCoffeePass.can_redeem_now 
                          ? 'bg-green-500 text-white animate-pulse' 
                          : customerCoffeePass.already_redeemed_today
                          ? 'bg-amber-500 text-white'
                          : 'bg-blue-500 text-white'
                      }`}>
                        <Coffee className="w-3 h-3" />
                        {customerCoffeePass.can_redeem_now 
                          ? 'FREE COFFEE READY' 
                          : customerCoffeePass.already_redeemed_today
                          ? 'Redeemed Today'
                          : 'Bula Pass'}
                      </span>
                    </div>
                    
                    {customerCoffeePass.can_redeem_now && (
                      <p className="text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 p-2 rounded">
                        <strong>Select any coffee from the menu</strong> - 8oz is FREE, 12oz upgrade +$3
                      </p>
                    )}
                    
                    {customerCoffeePass.already_redeemed_today && (
                      <p className="text-xs text-amber-600 dark:text-amber-400">
                        Already enjoyed their free coffee today
                      </p>
                    )}
                    
                    {!customerCoffeePass.can_redeem_now && !customerCoffeePass.already_redeemed_today && (
                      <p className="text-xs text-blue-600 dark:text-blue-400">
                        {customerCoffeePass.window_message || 'Outside redemption hours'}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sent Orders (Parked on this table) */}
          {tableInfo?.existingOrders?.length > 0 && (
            <div className="px-4 pb-2">
              <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="font-medium text-green-800 dark:text-green-300 text-sm">
                      Orders Sent to Kitchen
                    </span>
                  </div>
                  <span className="text-green-700 dark:text-green-400 font-bold">
                    ${(tableInfo.existingTotal || 0).toFixed(2)}
                  </span>
                </div>
                <div className="space-y-1">
                  {tableInfo.existingOrders.map((order, idx) => (
                    <div key={order.order_id || idx} className="text-xs text-green-700 dark:text-green-400 flex justify-between">
                      <span>Order #{order.order_number}</span>
                      <span>{order.items?.length || 0} items • ${(order.total || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-green-600 dark:text-green-500 mt-2 italic">
                  Add more items below or go to POS to collect payment
                </p>
              </div>
            </div>
          )}

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-4">
            {cart.length === 0 && !tableInfo?.existingOrders?.length ? (
              <div className="text-center py-12">
                <ShoppingBag className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400">No items yet</p>
              </div>
            ) : cart.length === 0 ? (
              <div className="text-center py-8">
                <Plus className="w-10 h-10 text-indigo-300 dark:text-indigo-600 mx-auto mb-2" />
                <p className="text-gray-500 dark:text-gray-400 text-sm">Add more items to this table</p>
              </div>
            ) : (
              <div className="space-y-3">
                {cart.map(item => (
                  <div key={item.item_id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 lg:p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-gray-900 dark:text-white text-sm lg:text-base">{item.name}</h4>
                      <button onClick={() => removeFromCart(item.item_id)} className="text-red-400 hover:text-red-500">
                        <Trash2 className="w-4 h-4 lg:w-5 lg:h-5" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 lg:gap-3">
                        <button
                          onClick={() => updateQuantity(item.item_id, -1)}
                          className="w-9 h-9 lg:w-10 lg:h-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-200"
                        >
                          <Minus className="w-4 h-4 lg:w-5 lg:h-5" />
                        </button>
                        <span className="font-medium text-gray-900 dark:text-white w-8 text-center text-lg">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.item_id, 1)}
                          className="w-9 h-9 lg:w-10 lg:h-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-200"
                        >
                          <Plus className="w-4 h-4 lg:w-5 lg:h-5" />
                        </button>
                      </div>
                      <span className="font-medium text-gray-900 dark:text-white text-base lg:text-lg">${((item.price || 0) * (item.quantity || 1)).toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Order Notes & Send Button */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            {/* Owner Order Toggle */}
            <button
              onClick={() => {
                setIsOwnerOrder(!isOwnerOrder);
                if (!isOwnerOrder) {
                  toast.success('👑 Owner Mode ON', { duration: 2000 });
                }
              }}
              className={`w-full mb-3 py-4 lg:py-5 px-4 rounded-lg flex items-center justify-center gap-2 font-bold text-base lg:text-lg transition-all ${
                isOwnerOrder 
                  ? 'bg-teal-600 text-white' 
                  : 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 border-2 border-teal-300 dark:border-teal-700'
              }`}
              data-testid="server-owner-order-btn"
            >
              <Crown className="w-5 h-5 lg:w-6 lg:h-6" />
              {isOwnerOrder ? '👑 OWNER MODE ON' : '👑 Owner Order'}
            </button>
            
            {/* Notes */}
            <div className="mb-4">
              <Input
                placeholder="Special instructions..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-white dark:bg-gray-700"
              />
            </div>

            {/* Total */}
            <div className="flex justify-between text-xl font-bold text-gray-900 dark:text-white mb-4">
              <span>Total</span>
              <span className={isOwnerOrder ? 'line-through text-gray-400' : ''}>${cartTotal.toFixed(2)}</span>
              {isOwnerOrder && <span className="text-teal-600">$0.00</span>}
            </div>

            {/* Send Order Button */}
            <Button
              onClick={sendToKitchen}
              disabled={cart.length === 0 || orderProcessing}
              className={`w-full h-16 lg:h-20 text-lg lg:text-xl disabled:opacity-50 ${
                isOwnerOrder 
                  ? 'bg-teal-600 hover:bg-teal-700 text-white' 
                  : 'bg-indigo-600 hover:bg-indigo-700 text-white'
              }`}
              data-testid="server-send-btn"
            >
              {orderProcessing ? (
                <Loader2 className="w-6 h-6 lg:w-7 lg:h-7 animate-spin" />
              ) : isOwnerOrder ? (
                <>
                  <Crown className="w-6 h-6 lg:w-7 lg:h-7 mr-2" />
                  Send Owner Order
                </>
              ) : (
                <>
                  <Send className="w-6 h-6 lg:w-7 lg:h-7 mr-2" />
                  Send to Stations
                </>
              )}
            </Button>
            
            {/* Back to Tables button - shown when there are sent orders */}
            {tableInfo?.existingOrders?.length > 0 && cart.length === 0 && (
              <Button
                onClick={() => {
                  setTableInfo(null);
                  setCart([]);
                  setTableSelectRefresh(prev => prev + 1);
                  setView('table-select');
                }}
                variant="outline"
                className="w-full h-14 lg:h-16 mt-2 border-green-500 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 text-base lg:text-lg"
                data-testid="server-back-to-tables-btn"
              >
                <ArrowLeft className="w-5 h-5 lg:w-6 lg:h-6 mr-2" />
                Done - Back to My Tables
              </Button>
            )}
            
            <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-2">
              Order prints to stations. Payment collected at checkout.
            </p>
          </div>
        </div>
      </div>
      
      {/* Item Customization Modal */}
      <ItemCustomizationModal
        item={selectedItem}
        open={showCustomization}
        onOpenChange={setShowCustomization}
        onAddToCart={handleAddCustomizedItem}
        availableAddOns={availableAddOns}
        theme={theme === 'dark' ? 'dark' : 'light'}
        showPrices={true}
        isAccessible={false}
        coffeePassStatus={(customerCoffeePass?.can_redeem_now || customerCoffeePass?.can_upgrade_to_redeem) ? customerCoffeePass : null}
        onCoffeePassRedeemed={(data) => {
          // Refresh the coffee pass status after redemption
          if (customer?.email || customer?.phone) {
            checkCoffeePassStatus(customer.email, customer.phone);
          }
        }}
      />
    </div>
  );
}
